import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { AuthContext } from '../../../contexts/AuthContext';
import { pageContext } from '../../../contexts/PageContext';
import * as ROUTES from '../../../constants/routes';

import { BoxContainer } from '../../../components/shared';

import styles from './Requestor.scss';

// Results are scoped to the logged-in requestor's own bookings server-side
// (see allToursTypes resolver), so no email is sent from the client.
const MY_TOURS = gql`
  query AllToursTypes($page: Page!) {
    allToursTypes(page: $page) {
      tours {
        id
        tourTypeName
        booking {
          id
          tourId
          status
          denyReason
        }
        calendar {
          id
          year
          month
          day
          times
        }
      }
      pageInfo {
        hasNextPage
        hasPrevPage
        allItems
      }
    }
  }
`;

const ALL_TOUR_TYPES = 'allTourTypes';

const RequestorDashboard = props => {
  const { history, type } = props;
  const auth = React.useContext(AuthContext);

  const [pending, setPending] = useState({ offset: 0, limit: 8 });
  const [approved, setApproved] = useState({ offset: 0, limit: 8 });
  const [denied, setDenied] = useState({ offset: 0, limit: 8 });

  const buildPage = (status, offset, limit) => ({
    offset,
    type,
    tourType: ALL_TOUR_TYPES,
    status,
    limit,
  });

  const onCompletedFor = (setSection, section) => res => {
    setSection({
      ...section,
      data: res.allToursTypes.tours,
      pageInfo: {
        hasNextPage: res.allToursTypes.pageInfo.hasNextPage,
        hasPrevPage: res.allToursTypes.pageInfo.hasPrevPage,
        allItems: res.allToursTypes.pageInfo.allItems,
      },
    });
  };

  const [pendingTours] = useLazyQuery(MY_TOURS, {
    fetchPolicy: 'network-only',
    onCompleted: onCompletedFor(setPending, pending),
    onError: err => {
      console.log(err);
    },
  });

  const [approvedTours] = useLazyQuery(MY_TOURS, {
    fetchPolicy: 'network-only',
    onCompleted: onCompletedFor(setApproved, approved),
    onError: err => {
      console.log(err);
    },
  });

  const [deniedTours] = useLazyQuery(MY_TOURS, {
    fetchPolicy: 'network-only',
    onCompleted: onCompletedFor(setDenied, denied),
    onError: err => {
      console.log(err);
    },
  });

  const fetchApiHandler = () => {
    setPending({ ...pending, offset: 0 });
    setApproved({ ...approved, offset: 0 });
    setDenied({ ...denied, offset: 0 });

    pendingTours({
      variables: { page: buildPage('pending', 0, pending.limit) },
    });
    approvedTours({
      variables: { page: buildPage('approvedFinalized', 0, approved.limit) },
    });
    deniedTours({
      variables: { page: buildPage('denied', 0, denied.limit) },
    });
  };

  const clickPaginationHandler = (section, dir) => {
    const step = (current, run, status) => {
      const offset = dir === 'next' ? current.offset + 1 : current.offset - 1;
      run({ variables: { page: buildPage(status, offset, current.limit) } });
      return offset;
    };

    if (section === 'pending') {
      setPending({
        ...pending,
        offset: step(pending, pendingTours, 'pending'),
      });
    } else if (section === 'finalized') {
      setApproved({
        ...approved,
        offset: step(approved, approvedTours, 'approvedFinalized'),
      });
    } else if (section === 'denied') {
      setDenied({
        ...denied,
        offset: step(denied, deniedTours, 'denied'),
      });
    }
  };

  useEffect(() => {
    fetchApiHandler();
  }, [type]);

  useEffect(() => {
    if (!auth.token) {
      const jwtAuth = JSON.parse(localStorage.getItem('jwtAuth'));
      !jwtAuth ? history.push(ROUTES.LOGIN) : null;
    }
  }, [auth]);

  return (
    <div className={styles.main}>
      <div className={styles.body}>
        <div className={styles.filter}>
          <button
            className={styles.btn__filter}
            onClick={() => fetchApiHandler()}
          >
            Refresh
          </button>
        </div>
        <pageContext.Provider value="requestor">
          {pending && (
            <BoxContainer
              clickPaginationHandler={clickPaginationHandler}
              items={pending}
              section="pending"
            />
          )}
          {approved && (
            <BoxContainer
              clickPaginationHandler={clickPaginationHandler}
              items={approved}
              section="finalized"
              title="approved"
            />
          )}
          {denied && (
            <BoxContainer
              clickPaginationHandler={clickPaginationHandler}
              items={denied}
              section="denied"
            />
          )}
        </pageContext.Provider>
      </div>
    </div>
  );
};

export default RequestorDashboard;
